import {
  BlueskyIcon,
  DynamicLinkService,
  LinkIcon,
  prodFrontendUrl,
  useCancellablePromise,
  useClipboard,
  useFileSaver,
} from '@geovelo-frontends/commons';
import { ContentCopy } from '@mui/icons-material';
import {
  Box,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  Skeleton,
  Typography,
} from '@mui/material';
import html2canvas from 'html2canvas';
import { useSnackbar } from 'notistack';
import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import {
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterShareButton,
  XIcon,
} from 'react-share';
import { styled } from 'styled-components';

import Button from './button';
import Dialog from './dialog';
import { DownloadIcon } from './icons';

type TProps = Omit<DialogProps, 'onClose'> & {
  disableLinkCopy?: boolean;
  disablePoorSharings?: boolean;
  disableQRCode?: boolean;
  downloadTitle?: string;
  enableDownload?: boolean;
  onClose: () => void;
  shareText?: string;
  url?: string | null;
  disableShortLink?: boolean;
  code?: string;
};

function ShareDialog({
  disablePoorSharings,
  disableLinkCopy,
  disableQRCode,
  url,
  shareText,
  disableShortLink,
  enableDownload,
  downloadTitle,
  code,
  ...props
}: TProps): React.JSX.Element {
  const [link, setLink] = useState<string>();
  const [downloadingQRCode, setQRCodeDownloading] = useState(false);
  const qrCodeRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { copy } = useClipboard(
    () => {
      enqueueSnackbar(
        t('commons.copied_to_clipboard', { context: copyCode.current ? 'code' : undefined }),
      );
      copyCode.current = false;
    },
    () => {
      enqueueSnackbar(
        t('commons.not_copied_to_clipboard', { context: copyCode.current ? 'code' : undefined }),
      );
      copyCode.current = false;
    },
  );
  const { cancellablePromise, cancelPromises } = useCancellablePromise();
  const { downloadBlob } = useFileSaver();
  const copyCode = useRef(false);

  useEffect(() => {
    if (props.open && url) {
      if (process.env.REACT_APP_ENV) createShortLink();
      else setLink(url);
    }

    return () => {
      cancelPromises();
      setLink(undefined);
    };
  }, [props.open, url]);

  async function createShortLink() {
    cancelPromises();

    if (!url) return;
    if (url.localeCompare(prodFrontendUrl) === 0 || disableShortLink) {
      setLink(url);
      return;
    }

    try {
      const link = await cancellablePromise(DynamicLinkService.createShortLink(url));
      setLink(link);
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        setLink(url);
      }
    }
  }

  async function handleQRCodeDownload() {
    setQRCodeDownloading(true);

    if (qrCodeRef.current) {
      const canvas = await html2canvas(qrCodeRef.current, {
        scale: 3,
        width: qrCodeRef.current.clientWidth,
        height: qrCodeRef.current.clientHeight,
        useCORS: true,
      });

      const blob = await new Promise<Blob>((resolve, reject) => {
        try {
          canvas.toBlob(
            (blob) => {
              if (!blob) throw new Error('no blob');
              else resolve(blob);
            },
            'image/png',
            1.0,
          );
        } catch (err) {
          reject(err);
        }
      });

      downloadBlob(`qr_code.png`, blob);
    }

    setQRCodeDownloading(false);
  }

  return (
    <Dialog fullWidth maxWidth="xs" {...props}>
      <DialogTitle>{t('geovelo.share_dialog.title')}</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <ButtonsWrapper>
          {link ? (
            <>
              <IconButton
                disableRipple
                href={`https://bsky.app/intent/compose?text=${encodeURIComponent([shareText, link].join(' '))}`}
                rel="noreferrer"
                sx={{
                  bgcolor: '#208bfe',
                  color: '#fff',
                  height: 32,
                  width: 32,
                }}
                target="_blank"
              >
                <BlueskyIcon />
              </IconButton>
              <TwitterShareButton hashtags={['geovelo']} title={shareText} url={link}>
                <XIcon round size={32} />
              </TwitterShareButton>
              {!disablePoorSharings && (
                <>
                  <FacebookShareButton hashtag="geovelo" url={link}>
                    <FacebookIcon round size={32} />
                  </FacebookShareButton>
                  <LinkedinShareButton source="Geovelo" title={shareText} url={link}>
                    <LinkedinIcon round size={32} />
                  </LinkedinShareButton>
                </>
              )}
              {enableDownload && url && (
                <IconButton
                  disableRipple
                  download={downloadTitle || true}
                  href={url}
                  sx={{
                    bgcolor: '#ddd',
                    color: '#333',
                    height: 32,
                    width: 32,
                  }}
                  target="_blank"
                >
                  <DownloadIcon />
                </IconButton>
              )}
            </>
          ) : (
            <>
              <Skeleton height={32} variant="circular" width={32} />
              {!disablePoorSharings && (
                <>
                  <Skeleton height={32} variant="circular" width={32} />
                  <Skeleton height={32} variant="circular" width={32} />
                </>
              )}
            </>
          )}
        </ButtonsWrapper>
        {!disablePoorSharings && (!disableQRCode || !disableQRCode) && (
          <>
            <Box alignItems="flex-start" display="flex" gap={2}>
              {!disableQRCode && (
                <Box flexShrink={0} height={128} ref={qrCodeRef} width={128}>
                  {link ? (
                    <QRCode
                      fgColor="#212121"
                      size={128}
                      style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                      value={link}
                      viewBox={`0 0 128 128`}
                    />
                  ) : (
                    <Skeleton height="100%" variant="rounded" width="100%" />
                  )}
                </Box>
              )}
              <Box display="flex" flexDirection="column" flexGrow={1} gap={1}>
                {!disableQRCode && (
                  <Button
                    fullWidth
                    color="primary"
                    disabled={downloadingQRCode}
                    onClick={() => handleQRCodeDownload()}
                    startIcon={<DownloadIcon />}
                    variant="outlined"
                  >
                    <Trans i18nKey="commons.actions.download_qr_code" />
                  </Button>
                )}
                {!disableLinkCopy && (
                  <Button
                    fullWidth
                    color="primary"
                    disabled={!link}
                    onClick={() => link && copy(link)}
                    startIcon={<LinkIcon />}
                    variant="outlined"
                  >
                    {t('commons.actions.copy_link')}
                  </Button>
                )}
              </Box>
            </Box>
            {code && (
              <Box alignItems="center" display="flex" flexDirection="row" gap={2}>
                <Typography>
                  <Trans i18nKey="geovelo.communities.invitation_link_dialog.code" />
                </Typography>
                <Button
                  color="primary"
                  endIcon={<ContentCopy />}
                  onClick={() => {
                    copy(code);
                  }}
                  variant="outlined"
                >
                  {code}
                </Button>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => props.onClose()}>{t('commons.actions.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

const ButtonsWrapper = styled.div`
  display: flex;
  flex-wrap: 1;
  gap: 8px;

  button {
    height: 32px;
  }
`;

export default ShareDialog;
